import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { BellOff, CheckCheck } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty';
import { Skeleton } from '@/components/ui/skeleton';
import { PillTabs } from '../../components/design-system/admin/PillTabs';
import { NotificationList } from '../../common/Header/NotificationList';
import { NotificationCategory, NotificationItem, isUnread, useNotificationsApi } from '../../common/hooks/use-notifications-api.hook';
import { useNotificationStore } from '../../common/stores/notification.store';
import { CATEGORY_ORDER, categoryKey, groupByDay } from './notification-presentation';

const PAGE_SIZE = 30;
type Filter = 'all' | 'unread' | NotificationCategory;

/**
 * Every notification, not only the last twenty the bell holds: by day, by
 * category, with what is still unread for this person marked. Opening one
 * marks it read and goes where it points.
 */
export function NotificationsInbox({ categories }: { categories: NotificationCategory[] }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const api = useNotificationsApi();
  const lastArrival = useNotificationStore((s) => s.lastArrival);

  const [filter, setFilter] = useState<Filter>('all');
  const [items, setItems] = useState<NotificationItem[] | null>(null);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [summary, setSummary] = useState<{ unread: number; byCategory: Record<string, number> } | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const query = useCallback(
    (p: number) =>
      api.findAll({
        page: p,
        limit: PAGE_SIZE,
        unreadOnly: filter === 'unread',
        category: filter !== 'all' && filter !== 'unread' ? filter : undefined,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // The API writes the words in the reader's language: a switch re-reads them.
    [filter, i18n.language],
  );

  const refreshSummary = useCallback(() => {
    api.getSummary().then(setSummary).catch(() => setSummary(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    setItems(null);
    query(1)
      .then((res) => {
        if (cancelled) return;
        setItems(res.data);
        setPage(1);
        setHasNext(res.pagination.hasNext);
      })
      .catch(() => !cancelled && setItems([]));
    refreshSummary();
    return () => {
      cancelled = true;
    };
  }, [query, refreshSummary, lastArrival]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const res = await query(page + 1);
      setItems((prev) => [...(prev ?? []), ...res.data]);
      setPage(page + 1);
      setHasNext(res.pagination.hasNext);
    } finally {
      setLoadingMore(false);
    }
  };

  const open = (item: NotificationItem) => {
    if (!isUnread(item)) return;
    setItems((prev) => prev?.map((i) => (i._id === item._id ? { ...i, read: true } : i)) ?? prev);
    useNotificationStore.getState().markReadLocal(item._id);
    api.markRead(item._id).then(refreshSummary).catch(() => undefined);
  };

  const markAll = async () => {
    const category = filter !== 'all' && filter !== 'unread' ? filter : undefined;
    await api.markAllRead(category).catch(() => undefined);
    setItems((prev) => prev?.map((i) => (!category || i.category === category ? { ...i, read: true } : i)) ?? prev);
    const { count } = await api.getUnreadCount().catch(() => ({ count: 0 }));
    useNotificationStore.getState().setUnreadCount(count);
    refreshSummary();
  };

  const options = useMemo(() => {
    const ordered = CATEGORY_ORDER.filter((c) => categories.includes(c));
    return [
      { value: 'all' as Filter, label: t('ARDI.NOTIFICATIONS.FILTER_ALL') },
      { value: 'unread' as Filter, label: t('ARDI.NOTIFICATIONS.FILTER_UNREAD'), count: summary?.unread },
      ...ordered.map((c) => ({ value: c as Filter, label: t(categoryKey(c)), count: summary?.byCategory?.[c] })),
    ];
  }, [categories, summary, t]);

  const groups = items ? groupByDay(items) : [];
  const anyUnread = (summary?.unread ?? 0) > 0;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <PillTabs value={filter} options={options} onChange={setFilter} ariaLabel={t('ARDI.NOTIFICATIONS.FILTER_LABEL') as string} />
        <Button variant="outline" size="sm" onClick={markAll} disabled={!anyUnread} className="self-start md:self-auto">
          <CheckCheck />
          {t('ARDI.NOTIFICATIONS.MARK_ALL')}
        </Button>
      </div>

      {!items ? (
        <div className="flex flex-col gap-2">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-16 rounded-xl" />)}
        </div>
      ) : !items.length ? (
        <Card className="py-2">
          <Empty className="border-0 py-12">
            <EmptyHeader>
              <EmptyMedia variant="icon"><BellOff /></EmptyMedia>
              <EmptyTitle>{t(filter === 'unread' ? 'ARDI.NOTIFICATIONS.EMPTY_UNREAD' : 'ARDI.NOTIFICATIONS.EMPTY')}</EmptyTitle>
              <EmptyDescription>{t('ARDI.NOTIFICATIONS.EMPTY_HINT')}</EmptyDescription>
            </EmptyHeader>
          </Empty>
        </Card>
      ) : (
        <div className="flex flex-col gap-5" data-testid="notifications-inbox">
          {groups.map((g) => (
            <section key={g.group} className="flex flex-col gap-2">
              <h2 className="px-1 text-xs font-medium text-muted-foreground">{t(`ARDI.NOTIFICATIONS.DAY.${g.group.toUpperCase()}`)}</h2>
              <Card className="gap-0 overflow-hidden py-0">
                <NotificationList items={g.items} variant="page" onOpen={open} onNavigate={(route) => navigate(route)} />
              </Card>
            </section>
          ))}
          {hasNext && (
            <Button variant="ghost" onClick={loadMore} disabled={loadingMore} className="self-center text-muted-foreground">
              {t('ARDI.NOTIFICATIONS.LOAD_MORE')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
